import type { CreaseAssignment, CreasePattern, CreasePatternMeta } from '@/origami/types';
import { planarize } from '@/origami/planarize';
import type { Vec2 } from '@/geometry/vec2';

export interface RawSegment {
  a: Vec2;
  b: Vec2;
  assignment: CreaseAssignment;
  foldAngle?: number | null;
}

/**
 * Build a planarised crease pattern from loose segments. Vertices are merged
 * and crossings resolved by the planariser, so example definitions can be
 * written as readable lists of lines without worrying about topology.
 */
export function patternFromSegments(
  segments: RawSegment[],
  meta: CreasePatternMeta = {},
): CreasePattern {
  const coords: Vec2[] = [];
  const edges: [number, number][] = [];
  const assignments: CreaseAssignment[] = [];
  const angles: (number | null)[] = [];
  for (const s of segments) {
    const ai = coords.push(s.a) - 1;
    const bi = coords.push(s.b) - 1;
    edges.push([ai, bi]);
    assignments.push(s.assignment);
    angles.push(s.foldAngle ?? null);
  }
  return planarize({
    verticesCoords: coords,
    edgesVertices: edges,
    edgesAssignment: assignments,
    edgesFoldAngle: angles,
    meta,
  }).pattern;
}

/** Boundary segments of a closed polygon. */
export function boundaryLoop(points: Vec2[]): RawSegment[] {
  const out: RawSegment[] = [];
  for (let i = 0; i < points.length; i++) {
    out.push({ a: points[i], b: points[(i + 1) % points.length], assignment: 'B' });
  }
  return out;
}

export function rectangleBoundary(width: number, height: number): RawSegment[] {
  return boundaryLoop([
    { x: 0, y: 0 },
    { x: width, y: 0 },
    { x: width, y: height },
    { x: 0, y: height },
  ]);
}

/**
 * Distance from `center` to the edge of an axis-aligned rectangle along a ray.
 * Used to clip crease rays exactly to the paper: a ray that overshoots the
 * boundary leaves a dangling stub outside the sheet, which is a real modelling
 * error, not a cosmetic one.
 */
export function rayToRectangle(
  center: Vec2,
  heading: number,
  width: number,
  height: number,
): Vec2 {
  const dx = Math.cos(heading);
  const dy = Math.sin(heading);
  let t = Infinity;
  if (Math.abs(dx) > 1e-12) {
    t = Math.min(t, ((dx > 0 ? width : 0) - center.x) / dx);
  }
  if (Math.abs(dy) > 1e-12) {
    t = Math.min(t, ((dy > 0 ? height : 0) - center.y) / dy);
  }
  if (!Number.isFinite(t)) t = 0;
  return { x: center.x + dx * t, y: center.y + dy * t };
}

/** Crease rays leaving a point, clipped to a rectangular sheet. */
export function raysFromPoint(
  center: Vec2,
  headingsDegrees: number[],
  assignments: CreaseAssignment[],
  width = 1,
  height = 1,
): RawSegment[] {
  return headingsDegrees.map((deg, i) => ({
    a: center,
    b: rayToRectangle(center, (deg * Math.PI) / 180, width, height),
    assignment: assignments[i] ?? 'U',
  }));
}

/** A blank rectangular sheet with no creases. */
export function blankSheet(width = 1, height = 1, title = 'Square sheet'): CreasePattern {
  return patternFromSegments(rectangleBoundary(width, height), {
    title,
    description: `${width} x ${height} sheet of paper.`,
    recommendedSolver: 'compliant',
  });
}
